import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, CheckCircle, Lock, Zap, RefreshCw, ChevronDown, ChevronUp, ShoppingCart } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useAppContext } from '../context/AppContext';
import { Toaster, toast } from 'sonner';
const sslProducts = [{
  id: 'dv',
  name: 'Domain Validation (DV) SSL',
  price: 9.99,
  idealFor: 'Personal websites, blogs',
  features: ['Domain validation only', 'Issued in minutes', 'Single domain', 'Browser padlock'],
  highlighted: false
}, {
  id: 'ov',
  name: 'Organization Validation (OV) SSL',
  price: 49.99,
  idealFor: 'Business websites, e-commerce',
  features: ['Organization verified', '1-3 day issuance', 'Company name in cert', '$250K warranty'],
  highlighted: true
}, {
  id: 'ev',
  name: 'Extended Validation (EV) SSL',
  price: 149.99,
  idealFor: 'Banks, large e-commerce, enterprises',
  features: ['Full business verification', 'Green address bar', 'Highest trust level', '$1.75M warranty'],
  highlighted: false
}, {
  id: 'wildcard',
  name: 'Wildcard SSL',
  price: 79.99,
  idealFor: 'Sites with multiple subdomains',
  features: ['Covers *.yourdomain.com', 'Unlimited subdomains', 'DV or OV available', 'Single certificate'],
  highlighted: false
}, {
  id: 'san',
  name: 'Multi-Domain (SAN) SSL',
  price: 99.99,
  idealFor: 'Multiple websites on one cert',
  features: ['Up to 100 domains', 'Single certificate', 'DV or OV available', 'Easy management'],
  highlighted: false
}];
const faqs = [{
  question: 'What is an SSL certificate?',
  answer: "An SSL (Secure Sockets Layer) certificate is a digital certificate that authenticates a website's identity and enables an encrypted connection. It ensures that all data passed between the web server and browsers remain private and integral."
}, {
  question: 'Do I need an SSL certificate?',
  answer: 'Yes. Modern web browsers flag websites without SSL as "Not Secure." SSL is essential for protecting user data, improving search engine rankings (SEO), and building trust with your visitors, especially if you collect passwords or payment information.'
}, {
  question: 'How long does it take to get an SSL certificate?',
  answer: 'Domain Validation (DV) certificates are typically issued within minutes. Organization Validation (OV) and Extended Validation (EV) certificates require manual verification of your business identity and can take 1-5 business days to be issued.'
}, {
  question: "What's the difference between DV, OV, and EV?",
  answer: 'DV (Domain Validation) only verifies that you own the domain. OV (Organization Validation) verifies your business identity. EV (Extended Validation) requires the most rigorous background checks and provides the highest level of trust, often displaying your company name in the browser.'
}];
export function SSLCertificates() {
  const {
    addToCart
  } = useAppContext();
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const handleAddToCart = (product: (typeof sslProducts)[0]) => {
    addToCart({
      packageId: `ssl_${product.id}_${Date.now()}`,
      name: product.name,
      category: 'domain',
      price: product.price,
      billing: 'yearly',
      quantity: 1
    });
    toast.success(`${product.name} added to cart!`);
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="min-h-screen bg-slate-950 flex flex-col" data-id="element-2693">
      <Navbar data-id="element-2694" />
      <Toaster theme="dark" position="top-right" data-id="element-2695" />

      <main className="flex-1 pt-32 pb-24" data-id="element-2696">
        {/* Hero Section */}
        <div className="container mx-auto px-4 md:px-6 mb-20 text-center max-w-4xl" data-id="element-2697">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20
        }} className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-emerald-500/20" data-id="element-2698">
            <Shield className="text-emerald-500 w-10 h-10" data-id="element-2699" />
          </motion.div>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6" data-id="element-2700">
            Secure Your Website with{' '}
            <span className="text-emerald-500" data-id="element-2701">SSL</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-lg text-slate-400 max-w-2xl mx-auto mb-12" data-id="element-2702">
            Protect your customers' data, boost your search engine rankings, and
            build trust with industry-leading SSL certificates.
          </motion.p>

          {/* Trust Indicators */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto" data-id="element-2703">
            <div className="flex flex-col items-center gap-2 p-4 bg-slate-900/50 rounded-2xl border border-slate-800" data-id="element-2704">
              <Lock className="text-slate-400" size={24} data-id="element-2705" />
              <span className="text-sm font-medium text-white" data-id="element-2706">
                256-bit Encryption
              </span>
            </div>
            <div className="flex flex-col items-center gap-2 p-4 bg-slate-900/50 rounded-2xl border border-slate-800" data-id="element-2707">
              <Shield className="text-slate-400" size={24} data-id="element-2708" />
              <span className="text-sm font-medium text-white" data-id="element-2709">
                Browser Trusted
              </span>
            </div>
            <div className="flex flex-col items-center gap-2 p-4 bg-slate-900/50 rounded-2xl border border-slate-800" data-id="element-2710">
              <Zap className="text-slate-400" size={24} data-id="element-2711" />
              <span className="text-sm font-medium text-white" data-id="element-2712">
                Instant Issuance
              </span>
            </div>
            <div className="flex flex-col items-center gap-2 p-4 bg-slate-900/50 rounded-2xl border border-slate-800" data-id="element-2713">
              <RefreshCw className="text-slate-400" size={24} data-id="element-2714" />
              <span className="text-sm font-medium text-white" data-id="element-2715">
                30-Day Refund
              </span>
            </div>
          </motion.div>
        </div>

        {/* Products Grid */}
        <div className="container mx-auto px-4 md:px-6 mb-24 max-w-6xl" data-id="element-2716">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-2717">
            {sslProducts.map((product, index) => <motion.div key={product.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className={`relative rounded-3xl p-8 flex flex-col ${product.highlighted ? 'bg-slate-800 border-2 border-fox-500 shadow-[0_0_30px_rgba(234,88,12,0.15)] transform md:-translate-y-4' : 'bg-slate-900 border border-slate-800'}`} data-id="element-2718">
                {product.highlighted && <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-fox-600 to-amber-500 text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg" data-id="element-2719">
                    Most Popular
                  </div>}

                <div className="mb-6 flex-1" data-id="element-2720">
                  <h3 className="text-xl font-heading font-bold text-white mb-2" data-id="element-2721">
                    {product.name}
                  </h3>
                  <p className="text-slate-400 text-sm h-10" data-id="element-2722">
                    Ideal for: {product.idealFor}
                  </p>
                </div>

                <div className="mb-8" data-id="element-2723">
                  <span className="text-4xl font-heading font-bold text-white" data-id="element-2724">
                    ${product.price}
                  </span>
                  <span className="text-slate-400" data-id="element-2725">/yr</span>
                </div>

                <ul className="space-y-4 mb-8 flex-1" data-id="element-2726">
                  {product.features.map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-2727">
                      <CheckCircle className={`shrink-0 mt-0.5 ${product.highlighted ? 'text-fox-500' : 'text-emerald-500'}`} size={18} data-id="element-2728" />
                      <span className="text-slate-300 text-sm" data-id="element-2729">{feature}</span>
                    </li>)}
                </ul>

                <button onClick={() => handleAddToCart(product)} className={`w-full py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${product.highlighted ? 'bg-gradient-to-r from-fox-600 to-amber-500 hover:from-fox-500 hover:to-amber-400 text-white shadow-[0_0_20px_rgba(234,88,12,0.3)]' : 'bg-slate-800 hover:bg-slate-700 text-white'}`} data-id="element-2730">
                  <ShoppingCart size={18} data-id="element-2731" />
                  Add to Cart
                </button>
              </motion.div>)}
          </div>
        </div>

        {/* FAQs */}
        <div className="container mx-auto px-4 md:px-6 max-w-3xl" data-id="element-2732">
          <div className="text-center mb-12" data-id="element-2733">
            <h2 className="text-3xl font-heading font-bold text-white mb-4" data-id="element-2734">
              Frequently Asked Questions
            </h2>
            <p className="text-slate-400" data-id="element-2735">
              Everything you need to know about SSL certificates.
            </p>
          </div>

          <div className="space-y-4" data-id="element-2736">
            {faqs.map((faq, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden" data-id="element-2737">
                <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full flex items-center justify-between p-6 text-left" data-id="element-2738">
                  <span className="text-lg font-bold text-white pr-8" data-id="element-2739">
                    {faq.question}
                  </span>
                  {openFaq === index ? <ChevronUp className="text-fox-500 shrink-0" data-id="element-2740" /> : <ChevronDown className="text-slate-500 shrink-0" data-id="element-2741" />}
                </button>
                <AnimatePresence data-id="element-2742">
                  {openFaq === index && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-2743">
                      <div className="p-6 pt-0 text-slate-400 leading-relaxed" data-id="element-2744">
                        {faq.answer}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>)}
          </div>
        </div>
      </main>

      <Footer data-id="element-2745" />
    </motion.div>;
}